<template>
	<card>
		<template slot="header">
			<h5 v-if="$slots.title || title" class="card-title">
				<slot name="title">
					{{title}}
				</slot>
			</h5>
		</template>
		<div>
			<div class="loading-spinner" v-if="isLoading">
				<vue-loaders-ball-spin-fade-loader color="white" scale="1.2"/>
			</div>
			<div class="ct-chart-daily" v-else>
				<v-chart class="chart" @select="onCalendarSelect" :option="option" style="margin: 0 auto;"/>
				<div class="total-value">
				</div>
			</div>
		</div>
	</card>
</template>
<script>
import Card from "./Card.vue";
import { mapActions, mapGetters } from "vuex";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { EffectScatterChart  } from "echarts/charts";
import {
	TitleComponent,
	TooltipComponent,
	LegendComponent,
	VisualMapComponent,
	CalendarComponent
} from "echarts/components";
import VChart  from "vue-echarts";
import QuerySetting from '@/mixins/querySetting';

use([
	CanvasRenderer,
	EffectScatterChart ,
	TitleComponent,
	TooltipComponent,
	LegendComponent,
	VisualMapComponent,
	CalendarComponent
]);

export default {
	name: "cost-predict-card",
	mixins: [QuerySetting],
	components: {
		Card,
		VChart
	},
	props: {
		title: {
			type: String,
			default: ""
		}
	},
	data() {
		return {
			isLoading: false,
			option: {
				tooltip: {
					position: 'top',
					backgroundColor: "#484E69",
					borderColor: "#484E69",
					axisPointer: {
						type: "shadow",
					},
					formatter: (params) => {
						return `Power: ${params.value[1].toFixed(2)} kWh`;
					},
					textStyle: {
						color: 'white'
					},
				},
				calendar: {
					orient: 'vertical',
					range: '1900-01',
					yearLabel: {
						show: false
					},
					monthLabel: {
						show: true,
						color: 'white'
					},
					itemStyle: {
						borderWidth: 0.1,
						borderColor: 'white',
						color: '#6200EE',
						opacity: 0.6
					},
					dayLabel: {
						firstDay: 1,
						nameMap: 'cn',
						show: true,
						color: 'white'
					},
					splitLine: {
						show: true,
						lineStyle: {
							color: 'white',
							width: 0.1,
							type: 'solid'
						}
					},
					cellSize: 40,
					left:'10%',
					right: '10%',
				},
				series: [{
					type: 'effectScatter',
					coordinateSystem: 'calendar',
					rippleEffect: {
						color: '#D686EA',
						brushType: 'fill'
					},
					label: {
						show: true,
						color: "white",
						formatter: function (params) {
							return params.data[0].slice(8, 10);
						},
					},
					data: []
				}]
			}
		};
	},
	computed: {
		...mapGetters({
			periodTime: 'settime/getPeriodTime',
			defaultSiteId: 'auth/getDefaultSiteId'
		}),
		chart() {
			return this.$refs.chart;
		}
	},
	watch: {
		periodTime(newValue) {
			this.getCostData(newValue);
		},
		async defaultSiteId(newValue) {
			this.getCostData(this.periodTime);
		}
	},
	methods: {
		...mapActions({
			getData: 'influx/getInfluxData',
			getDailyMapData: 'influx/getDailyMapData',
			setMapDay: 'settime/setMapDay'
		}),
		onCalendarSelect(params) {
			const today = new Date();
			const year = today.getFullYear();
			const month = today.getMonth() + 1;
			let monthString = '', dayString = '';
			if (month < 10) {
				monthString = '0' + month;
			} else {
				monthString = month;
			}

			const day = parseInt(params.dataIndexInside) + 1;
			
			if (day < 10) {
				dayString = '0' + day;
			} else {
				dayString = day
			}

			let clickedDay = `${year}-${monthString}-${dayString}T01:00:00Z`;
			this.setMapDay(clickedDay);
			this.$router.push({name: 'realtimepower'});
		},
		async getCostData(value) {
			this.isLoading = true;
			let totalPower = 0;
			let powerData = [];
			const data = await this.getDailyMapData();
			let time = new Date().toISOString().slice(0, 7);
			let today = new Date();
			let lastDay = new Date(today.getFullYear(), today.getMonth()+1, 0);
			let lastDate = lastDay.getDate();
			let firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
			let changedData = [];

			let underOne = false,
			    betweenOneAndTen = false,
				betweenTenAndTwenty = false,
				betweenTwentyAndFourty = false,
				betweenFourtyAndHundred = false,
				betweenHundredAndEightHundred = false,
				aboveEightHundred = false;
			

			for (let i = 0; i < lastDate; i++) {
				firstDay.setDate(firstDay.getDate() + 1);
				let timeString = firstDay.toISOString().slice(0, 10);
				let day = data.find(i => i[0] === timeString);
				let value = 0;
				if (day) {
					value = day[1];
				}

				if (parseFloat(value) > 800) {
					aboveEightHundred = true;
				} 

				if (parseFloat(value) > 100 && parseFloat(value) < 801) {
					betweenHundredAndEightHundred = true;
				}

				if (parseFloat(value) > 40 && parseFloat(value) < 101) {
					betweenFourtyAndHundred = true;
				}

				if (parseFloat(value) > 20 && parseFloat(value) < 41) {
					betweenTwentyAndFourty = true;
				}

				if (parseFloat(value) > 10 && parseFloat(value) < 21) {
					betweenTenAndTwenty = true;
				}

				if (parseFloat(value) > 1 && parseFloat(value) < 11) {
					betweenOneAndTen = true;
				}

				if (parseFloat(value) < 1) {
					underOne = true;
				}

				let item = [
					firstDay.toISOString().slice(0, 10),
					value
				];
				changedData.push(item)
			}
			this.isLoading = false;
			this.option.calendar.range = time;
			
			
			if (aboveEightHundred) {
				this.option.series[0].symbolSize = function (val) {
					let value = val[1];
					value = val[1] / 10;
					return value;
				}
			}

			if (!aboveEightHundred && betweenHundredAndEightHundred) {
				this.option.series[0].symbolSize = function (val) {
					let value = val[1];
					value = val[1] / 4;
					return value;
				}
			}

			if (!aboveEightHundred && 
				!betweenHundredAndEightHundred &&
				betweenFourtyAndHundred) {
				this.option.series[0].symbolSize = function (val) {
					let value = val[1];
					value = val[1] / 2;
					return value;
				}
			}

			if (!aboveEightHundred && 
				!betweenHundredAndEightHundred &&
				!betweenFourtyAndHundred &&
				betweenTwentyAndFourty) {
				this.option.series[0].symbolSize = function (val) {
					let value = val[1];
					value = val[1] * 1.5;
					return value;
				}
			}

			if (!aboveEightHundred && 
				!betweenHundredAndEightHundred &&
				!betweenFourtyAndHundred &&
				!betweenTwentyAndFourty &&
				betweenTenAndTwenty) {
				this.option.series[0].symbolSize = function (val) {
					let value = val[1];
					value = val[1] * 2;
					return value;
				}
			}

			if (!aboveEightHundred && 
				!betweenHundredAndEightHundred &&
				!betweenFourtyAndHundred &&
				!betweenTwentyAndFourty &&
				!betweenTenAndTwenty &&
				betweenOneAndTen) {
				this.option.series[0].symbolSize = function (val) {
					let value = val[1];
					value = val[1] * 4;
					return value;
				}
			}

			if (!aboveEightHundred && 
				!betweenHundredAndEightHundred &&
				!betweenFourtyAndHundred &&
				!betweenTwentyAndFourty &&
				!betweenTenAndTwenty &&
				!betweenOneAndTen &&
				underOne) {
				this.option.series[0].symbolSize = function (val) {
					let value = val[1];
					value = val[1] * 10;
					return value;
				}
			}
			this.option.series[0].data = changedData;
		}
	},
	mounted() {
		this.getCostData(this.periodTime);
		this.$nextTick(() => {
			window.setInterval(() => {
				this.getCostData(this.periodTime);
			},30000);
		});
	}
};
</script>
<style lang="scss" scoped>
.chart {
	height: 300px;
}
.total-value {
	position: absolute;
	top: 40%;
	left: 28%;
	text-align: center;

	span {
		font-size: 22px;
		font-family: 'Nunito Sans Regular';
		font-weight: 600;
		line-height: 1.1;
	}
}
</style>
